import type { ProductInfo } from "@expert/contentful";
import { Badge, Card, Flex, Text } from "@mantine/core";
import type { JSX } from "react";
import type { EligibleProduct } from "@/sales-tools";
import { DeclinedIcon } from "@/sales-tools/icons/DeclinedIcon";
import { EnrolledIcon } from "@/sales-tools/icons/EnrolledIcon";
import { usePartner } from "@/sdk";
import { SaleProgress } from "../../home-product/types";
import styles from "./BaseOfferCard.module.css";
import { ATTIcon, VerizonIcon } from "./icons";
import { OptimumIcon } from "./icons/OptimumIcon";

interface OfferCardProps {
    product: EligibleProduct;
    productInfo: ProductInfo | undefined;
    onClick: (product: EligibleProduct) => void;
    selected?: boolean;
}

function determineCardStyle(saleProgess: SaleProgress | undefined) {
    if (saleProgess === SaleProgress.Success) {
        return styles.enrolled;
    } else if (saleProgess === SaleProgress.Declined) {
        return styles.declined;
    }

    return "";
}
export function OfferBaseCard({ product, productInfo, onClick, selected }: OfferCardProps): JSX.Element | null {
    const partner = usePartner();
    const { saleProgress } = product;
    const handleClick = () => {
        if (saleProgress === SaleProgress.InProgress) {
            onClick(product);
        }
    };
    const getIcon = (partner: string) => {
        switch (partner) {
            case "verizon":
            case "fios":
                return <VerizonIcon />;
            case "att":
                return <ATTIcon />;
            case "optimum":
                return <OptimumIcon />;
            default:
                return null;
        }
    };

    // Return nothing if contentful data is loading
    if (productInfo == null) {
        return null;
    }

    return (
        <Card
            classNames={{
                root: `${styles.cardContainer} ${determineCardStyle(saleProgress)} ${selected ? styles.selected : ""}`,
            }}
            onClick={handleClick}
            radius="md"
            shadow="sm"
        >
            <div>
                {product.saleProgress === SaleProgress.Failure && (
                    <Badge
                        color={saleProgress === SaleProgress.Failure ? "error.3" : "warning.3"}
                        fz="sm"
                        fw={400}
                        className={styles.badge}
                        tt={"none"}
                        lh="sm"
                    >
                        {saleProgress === SaleProgress.Failure ? "Ineligible" : "Check eligibility ⚠"}
                    </Badge>
                )}
            </div>
            <div>{getIcon(partner)}</div>
            <Text classNames={{ root: styles.cardText }} size="xs">
                {productInfo?.displayName}
            </Text>
            <Flex
                className={
                    ([SaleProgress.InProgress, SaleProgress.Failure] as SaleProgress[]).includes(saleProgress)
                        ? ""
                        : styles.hidden
                }
                classNames={{ root: styles.planImageSection }}
            >
                {productInfo?.logo && (
                    <img alt={`${productInfo?.displayName} icon`} className={styles.logo} src={productInfo.logo} />
                )}
            </Flex>
            {!([SaleProgress.InProgress, SaleProgress.Failure] as SaleProgress[]).includes(saleProgress) ? (
                <Flex classNames={{ root: styles.offerStatusSection }}>
                    {saleProgress === SaleProgress.Success ? (
                        <EnrolledIcon width={20} height={20} />
                    ) : (
                        <DeclinedIcon width={20} height={20} />
                    )}
                    <Text classNames={{ root: styles.planDispositionText }} size="sm">
                        {saleProgress === SaleProgress.Success ? "Enrolled" : "Declined"}
                    </Text>
                </Flex>
            ) : null}
        </Card>
    );
}
